import React from "react";
import DOMPurify from "dompurify";
import Sidebar from "../../menu/Sidebar";
import Topbar from "../../menu/Topbar";
import Api from "../../Services/Api";
import RealgemsSidebar from "../../realgems/RealgemsSidebar";
import "./MachinesSupport.css";

const getYouTubeEmbedUrl = (value = "") => {
  try {
    const url = new URL(String(value).trim());
    const hostname = url.hostname.replace(/^www\./, "").toLowerCase();
    let videoId = "";

    if (hostname === "youtu.be") {
      videoId = url.pathname.split("/").filter(Boolean)[0] || "";
    } else if (hostname === "youtube.com" || hostname === "m.youtube.com") {
      if (url.pathname === "/watch") videoId = url.searchParams.get("v") || "";
      else if (/^\/(embed|shorts|live)\//.test(url.pathname)) {
        videoId = url.pathname.split("/").filter(Boolean)[1] || "";
      }
    }

    return /^[a-zA-Z0-9_-]{6,}$/.test(videoId)
      ? `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`
      : "";
  } catch (_err) {
    return "";
  }
};

const MachinesSupport = ({ audience = "LICENSED", realgems = false, catalogType = "MACHINE" }) => {
  const isLinenWater = catalogType === "LINEN_WATER";
  const isEssence = catalogType === "ESSENCE";
  const catalogTitle = isLinenWater ? "Água de lençol" : isEssence ? "Essências" : "Máquinas";
  const itemName = isLinenWater ? "água de lençol" : isEssence ? "essência" : "máquina";
  const apiBase = isLinenWater ? "/realgems/linen-waters" : isEssence ? "/public/essences" : "/public/machine-videos";
  const [menuClose, setMenuClose] = React.useState(false);
  const [machines, setMachines] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [activeVideo, setActiveVideo] = React.useState(null);

  const toggleMenu = () => {
    setMenuClose((prev) => !prev);
  };

  React.useEffect(() => {
    let mounted = true;

    const loadMachines = async () => {
      try {
        setLoading(true);
        const response = await Api.get(apiBase, {
          params: { audience },
        });
        const items = Array.isArray(response?.data?.items) ? response.data.items : [];

        if (!mounted) return;
        setMachines(items);
      } catch (err) {
        console.error("Erro ao carregar máquinas de apoio:", err);

        if (!mounted) return;
        setMachines([]);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadMachines();

    return () => {
      mounted = false;
    };
  }, [audience, apiBase]);

  React.useEffect(() => {
    if (!activeVideo) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setActiveVideo(null);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeVideo]);

  return (
    <div className="app-container">
      {realgems ? (
        <RealgemsSidebar close={menuClose} />
      ) : (
        <Sidebar
          close={menuClose}
          machinesSupportActive={isEssence ? "" : "active"}
          essencesSupportActive={isEssence ? "active" : ""}
        />
      )}

      <main className="main-content">
        <Topbar page={catalogTitle} onToggleMenu={toggleMenu} />

        <section className="machines-support-card">
          <div className="machines-support-header">
            <div>
              <h1 className="machines-support-title">{catalogTitle}</h1>
              <p className="machines-support-description">
                {isLinenWater ? "Fragrâncias para perfumar enxovais e acolher hóspedes com conforto. Condições exclusivas Realgems com 12% de desconto sobre o valor do site." : `Confira os materiais de apoio disponíveis para cada ${itemName}, com descrição do produto e links úteis.`}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="machines-support-empty-state">Carregando materiais de apoio...</div>
          ) : machines.length ? (
            <div className="machines-support-grid">
              {machines.map((machine) => (
                <article className="machine-support-item" key={machine.id}>
                  <div className="machine-support-item-image">
                    {machine.imageUrl ? (
                      <img src={machine.imageUrl} alt={machine.title} />
                    ) : (
                      <div className="machine-support-item-placeholder">Sem foto</div>
                    )}
                  </div>

                  <div className="machine-support-item-body">
                    <div className="machine-support-item-copy">
                      <h2>{machine.title || `${itemName} sem nome`}</h2>
                      {machine.description ? (
                        <div
                          className="machine-support-rich-description"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(machine.description),
                          }}
                        />
                      ) : (
                        <p>Descrição não informada.</p>
                      )}
                    </div>

                    <div className="machine-support-item-links">
                      {(machine.links || []).map((link) => {
                        const embedUrl = getYouTubeEmbedUrl(link.url);

                        return embedUrl ? (
                          <button
                            key={link.id}
                            type="button"
                            onClick={() => setActiveVideo({
                              embedUrl,
                              title: link.label || machine.title || "Vídeo",
                            })}
                          >
                            {link.label}
                          </button>
                        ) : (
                          <a key={link.id} href={link.url} target="_blank" rel="noreferrer">
                            {link.label}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="machines-support-empty-state">
              Nenhuma {itemName} foi configurada no momento. Procure o super administrador.
            </div>
          )}
        </section>

        {activeVideo ? (
          <div
            className="machine-video-modal-backdrop"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setActiveVideo(null);
            }}
          >
            <section
              className="machine-video-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="machine-video-modal-title"
            >
              <div className="machine-video-modal-header">
                <h2 id="machine-video-modal-title">{activeVideo.title}</h2>
                <button type="button" onClick={() => setActiveVideo(null)} aria-label="Fechar vídeo">
                  Fechar
                </button>
              </div>
              <div className="machine-video-modal-player">
                <iframe
                  src={activeVideo.embedUrl}
                  title={activeVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </section>
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default MachinesSupport;
